<template>
  <div>
    <v-expansion-panels
      flat
      accordion
      multiple
      class="expansion-panel-wrapper"
      v-model="panelModel"
    >
      <v-expansion-panel
        v-for="item in structure"
        :key="item.eventTrigger"
        :value="item.eventTrigger"
      >
        <v-expansion-panel-title
          class="expansion-panel-header pl-4 pt-2 pb-2"
          @click="handlePanelClick(item.eventTrigger)"
        >
          <template v-slot:actions />
          <template v-slot:default="{ expanded }">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-icon :class="{ rotated: !expanded }" icon="$chevronUp"></v-icon>
                <div class="ml-1 subtitle2">
                  {{ $t(`tickets.eventSubTriggers.${item.eventTrigger}`) }}
                </div>
              </div>
              <div class="body2 ml-1">
                {{ item.numberOfFindings }} {{ $t("ticketDetails.findings") }}
              </div>
            </div>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <file-findings-table
            :file-findings="findingsData[item.eventTrigger]?.items || []"
            :file-findings-loading="findingsData[item.eventTrigger]?.loading || false"
            :file-findings-total="findingsData[item.eventTrigger]?.total || 0"
            :hide-header="true"
            :show-last-seen="showLastSeen"
            @load-more="handlePaginationChange(item.eventTrigger)"
          >
          </file-findings-table>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType } from "vue";
import {
  type DeviceDlpUnifiedFindingStructureItem,
  type FileFinding,
} from "@/_store/tickets/ticket.module.ts";
import { TicketTrigger } from "@/constants/tickets.ts";
import FileFindingsTable from "./FileFindingsTable.vue";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";

export interface UnifiedFileFindingsDataItem {
  loading: boolean;
  items: FileFinding[];
  total: number;
}

export type UnifiedFileFindingsData = Partial<Record<TicketTrigger, UnifiedFileFindingsDataItem>>;

export default defineComponent({
  components: {
    FileFindingsTable,
  },
  props: {
    findingsData: {
      type: Object as PropType<UnifiedFileFindingsData>,
      required: true,
    },
    structure: {
      type: Array as PropType<DeviceDlpUnifiedFindingStructureItem[]>,
      required: true,
    },
    showLastSeen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["panel-expanded", "panel-collapsed", "page-changed"],
  setup(_, { emit }) {
    const panelModel = ref<TicketTrigger[]>([]);

    const handlePanelClick = (eventTrigger: TicketTrigger) => {
      const isExpanded = panelModel.value.includes(eventTrigger);

      if (isExpanded) {
        emit("panel-expanded", eventTrigger);
      } else {
        emit("panel-collapsed", eventTrigger);
      }

      trackGtmEvent(GtmAction.TICKETS, `${GTMParams.DEVICE_FINDINGS_CLICKED}`);
    };

    const handlePaginationChange = (eventTrigger: TicketTrigger) => {
      emit("page-changed", eventTrigger);
    };

    return {
      panelModel,
      handlePanelClick,
      handlePaginationChange,
    };
  },
});
</script>

<style scoped lang="scss">
.expansion-panel-header {
  height: 56px;
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 0 !important;
}

.v-expansion-panel:not(.v-expansion-panel--active):not(:last-child) {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 0 !important;
}

:deep(*) {
  .v-expansion-panel-text__wrapper {
    padding: 0 !important;
  }
  #chevron-col {
    padding-left: 40px !important;
  }
}
</style>
